// Turns an automated SMS (bank, UPI, card, FASTag, insurance, utility bills...) into an
// entry the person can review. Everything here runs on the phone; message text is
// never sent anywhere.
//
// parseBankSms({ address, body, date }) -> null (not useful) or
//   {
//     kind: "expense" | "income" | "bill",   bill = something coming up that still has to be paid
//     amount, payee, type, description,
//     date: "YYYY-MM-DD",                    when it happened, or the due date for a bill
//     autopay: boolean,                      bill only: "will be debited" notices
//     bank                                   sender name, e.g. HDFCBK
//   }

const AMOUNT = /(?:rs\.?|inr|₹)\s*([\d,]+(?:\.\d{1,2})?)/i;
const TOTAL_DUE = /total\s+(?:amt|amount)\s+due[^\d₹]*(?:rs\.?|inr|₹)?\s*([\d,]+(?:\.\d{1,2})?)/i;

// Messages that are noise, or where no money actually moved.
const IGNORE = [
  /\botp\b/,
  /one[\s-]?time\s+password/,
  /verification\s+code/,
  /do\s+not\s+share/,
  /\bmandate\b/,
  /\bhas\s+requested\b|\brequest(?:ed)?\s+(?:money|rs)/,
  /\bpre-?approved\b|\bapply\s+now\b|\bcongratulations\b|\boffer\b|\bwin\b|\bcashback\s+of\b/,
  /\bfailed\b|\bdeclined\b|\bunsuccessful\b|\bnot\s+debited\b|\bwill\s+not\s+be\b|\brejected\b/,
];

// Something is coming up and still has to be paid.
const UPCOMING =
  /\b(?:is|are|was)\s+due\b|\bdue\s+(?:on|by|date|for)\b|\bpayable\b|\bwill\s+be\s+(?:auto-?)?(?:debited|deducted|charged)\b|\bminimum\s+(?:amount\s+)?due\b|\btotal\s+(?:amt|amount)\s+due\b|\bpay\s+(?:before|by)\b|\bbill\s+(?:of|for|amount|generated)\b|\bpremium\s+(?:of|is|amount)\b|\brenewal\b|\bemi\s+of\b/;

// "Thank you for your payment", "premium received": the person paid something.
const PAYMENT_ACK =
  /(?:thank(?:s)?\s+you\s+for|thanks\s+for)\b.{0,40}\b(?:payment|paying)|\bpayment\b.{0,60}\b(?:received|successful|successfully|done|processed|confirmed)|\b(?:premium|bill|emi|installment)\b.{0,60}\b(?:received|paid|processed)|\bsuccessfully\s+(?:paid|debited)/;

const DEBIT_WORDS = /\b(debited|debit|spent|withdrawn|withdrawal|paid|purchase|purchased|sent|deducted|txn|transferred|toll)\b/;
const CREDIT_WORDS = /\b(credited|credit|received|deposited|refund|refunded|salary)\b/;

// First match wins, so more specific rules go first. [pattern, type, label]
const CATEGORIES = [
  [/fastag|\bnetc\b|\bnhai\b|toll\s+(?:plaza|charge)|toll\s+at\b/, "Bills", "FASTag toll"],
  [/\blic\b|licind|licindia|life\s+insurance\s+corporation/, "Investments", "LIC premium"],
  [/insurance|policy\s*(?:no|number)|\bpremium\b/, "Investments", "Insurance premium"],
  [/\bsip\b|mutual\s+fund|\bnps\b|\bppf\b|demat|zerodha|groww/, "Investments", ""],
  [/electricity|\bbescom\b|\bmsedcl\b|\btneb\b|discom|tata\s+power|adani\s+electricity|power\s+(?:bill|supply)/, "Bills", "Electricity bill"],
  [/postpaid|mobile\s+bill|prepaid|recharge|\bjio\b|airtel|vodafone|\bbsnl\b/, "Bills", "Mobile bill"],
  [/broadband|fibernet|\bwifi\b|internet\s+bill/, "Bills", "Internet bill"],
  [/\bgas\b|indane|bharat\s?gas|hp\s+gas|\bpng\b|mahanagar/, "Bills", "Gas bill"],
  [/water\s+(?:bill|board|supply|charges)|\bbwssb\b/, "Bills", "Water bill"],
  [/\bemi\b|\bloan\b/, "Loans", "Loan EMI"],
  [/netflix|prime\s+video|amazon\s+prime|hotstar|spotify|youtube|disney|sonyliv|zee5|jiocinema/, "Subscription", ""],
  [/credit\s+card|card\s+(?:ending|no|xx)|\bcard\s+bill/, "Card", "Credit card bill"],
];

// Everyday spending, by merchant or words in the message (UPI handles like
// "swiggy@icici" included). Used when none of the bill categories above match, so a
// payment isn't just "Purchase". First match wins. [pattern, type]
const SPENDING = [
  [/swiggy(?!\s*instamart)|zomato|domino|mcdonald|\bkfc\b|burger\s*king|pizza|starbucks|\bcafe\b|coffee|restaurant|\bdhaba\b|biryani|haldiram|barbeque|eatsure|faasos|chaayos|\bbar\b|\bpub\b|food/, "Food & Dining"],
  [/instamart|bigbasket|big\s*basket|blinkit|grofers|zepto|dmart|d-mart|jiomart|reliance\s*fresh|more\s*(?:retail|supermarket)|spencer|nature'?s\s*basket|supermarket|hypermarket|grocer|kirana|provision|milk|dairy|vegetable|fruits|\bbbnow\b|swiggy\s*instamart/, "Groceries"],
  [/amazon(?!\s*prime)|flipkart|myntra|ajio|meesho|nykaa|tata\s*cliq|snapdeal|lenskart|decathlon|croma|reliance\s*digital|vijay\s*sales|ikea|pantaloons|lifestyle|westside|\bzara\b|h\s*&\s*m|\bmax\s*fashion|shoppers\s*stop|firstcry|shopping|\bmall\b/, "Shopping"],
  [/\buber\b|\bola\b|olacabs|rapido|namma\s*yatri|blusmart|\bmetro\b|\bbmtc\b|\bbest\s*bus|parking|petrol|diesel|\bfuel\b|indian\s*oil|\biocl?\b|\bhpcl\b|\bbpcl\b|hindustan\s*petroleum|bharat\s*petroleum|\bshell\b|nayara|filling\s*station|\bcng\b/, "Transport"],
  [/irctc|makemytrip|make\s*my\s*trip|goibibo|cleartrip|yatra|easemytrip|ixigo|redbus|abhibus|indigo|air\s*india|vistara|akasa|spicejet|airasia|\boyo\b|airbnb|\bhotel\b|booking\.com|agoda|trivago/, "Travel"],
  [/bookmyshow|\bpvr\b|inox|cinepolis|cinema|movie|paytm\s*insider|gaming|steam|playstation|\bxbox\b/, "Entertainment"],
  [/pharma|pharmacy|chemist|medical|medplus|apollo|1mg|tata\s*1mg|netmeds|pharmeasy|hospital|clinic|diagnostic|\blab\b|dr\.?\s*lal|thyrocare|practo|dental|\bhealth\b/, "Health"],
  [/salon|\bspa\b|parlour|parlor|barber|urban\s*company|urbanclap|grooming|cosmetic/, "Personal Care"],
  [/school|college|universit|tuition|coaching|udemy|coursera|byju|unacademy|vedantu|upgrad|course\s*fee|exam\s*fee|stationery|books?\b/, "Education"],
  [/donation|charity|temple|mandir|gurudwara|church|mosque|trust\b|gift/, "Gifts & Donations"],
];

// The everyday category for a payment, from its merchant and message; null if unknown.
export const spendingType = (text) => {
  const lower = String(text || "").toLowerCase();
  const hit = SPENDING.find(([pattern]) => pattern.test(lower));
  return hit ? hit[1] : null;
};

const MONTHS = { jan: 0, feb: 1, mar: 2, apr: 3, may: 4, jun: 5, jul: 6, aug: 7, sep: 8, oct: 9, nov: 10, dec: 11 };

const pad = (n) => String(n).padStart(2, "0");
const toIso = (y, m, d) => `${y}-${pad(m + 1)}-${pad(d)}`;
const isoFromMs = (ms) => {
  const d = new Date(Number(ms) || Date.now());
  return toIso(d.getFullYear(), d.getMonth(), d.getDate());
};

const validDate = (y, m, d) => {
  const dt = new Date(y, m, d);
  return dt.getFullYear() === y && dt.getMonth() === m && dt.getDate() === d;
};

// Finds the first date written inside the message. A missing year means "the next
// time that date comes around".
const dateInText = (text, refMs) => {
  const ref = new Date(Number(refMs) || Date.now());
  const fullYear = (y) => (y < 100 ? 2000 + y : y);
  const withYear = (m, d, y) => {
    let year = y === undefined ? ref.getFullYear() : fullYear(y);
    if (y === undefined && new Date(year, m, d) < new Date(ref.getFullYear(), ref.getMonth(), ref.getDate() - 30)) year += 1;
    return validDate(year, m, d) ? toIso(year, m, d) : null;
  };

  let m = /\b(\d{1,2})[-/.](\d{1,2})[-/.](\d{2,4})\b/.exec(text);
  if (m) {
    const iso = withYear(Number(m[2]) - 1, Number(m[1]), Number(m[3]));
    if (iso) return iso;
  }
  m = /\b(\d{1,2})(?:st|nd|rd|th)?[\s-]+(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)[a-z]*\.?(?:[\s,-]+(\d{2,4}))?\b/i.exec(text);
  if (m) {
    const iso = withYear(MONTHS[m[2].toLowerCase()], Number(m[1]), m[3] ? Number(m[3]) : undefined);
    if (iso) return iso;
  }
  m = /\b(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)[a-z]*\.?\s+(\d{1,2})(?:st|nd|rd|th)?(?:,?\s+(\d{4}))?\b/i.exec(text);
  if (m) {
    const iso = withYear(MONTHS[m[1].toLowerCase()], Number(m[2]), m[3] ? Number(m[3]) : undefined);
    if (iso) return iso;
  }
  return null;
};

const cleanName = (raw) => {
  const text = raw.replace(/[^\w@&.'\- ]/g, " ").replace(/\s+/g, " ").trim();
  if (!text) return "";
  if (text.includes("@")) return text.toLowerCase();
  return text
    .toLowerCase()
    .split(" ")
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : w))
    .join(" ");
};

const findMerchant = (body, kind) => {
  const upi = /\bvpa\s+([\w.-]+@\w+)/i.exec(body);
  if (upi) return cleanName(upi[1]);

  const stop = "(?=\\s+(?:on|via|ref|using|from|upi|txn|trf|towards|avl|bal|info|a\\/c|ac|acct|card|for|dated|umn|\\d{1,2}[-/]|\\.|,|;)|\\.\\s|[.,;]?$)";
  const preposition = kind === "income" ? "(?:from|by)" : "(?:at|to|towards|for)";
  // Try each "from X" / "at X" in turn, skipping ones that are really an amount or an account.
  const notAName = /^(?:(?:a\/c|ac|acct|account|your|xx+\w*|card|bank|rs|inr|policy|bill|premium|the|toll|next)\b|\d)/i;
  const named = new RegExp(`\\b${preposition}\\s+([A-Za-z0-9&.'\\- ]{2,40}?)${stop}`, "gi");
  for (const match of body.matchAll(named)) {
    if (!notAName.test(match[1])) return cleanName(match[1]);
  }
  const info = /\binfo[:\s-]+([A-Za-z0-9&.'\- ]{2,40}?)(?:\.|,|;|\s{2}|$)/i.exec(body);
  return info ? cleanName(info[1]) : "";
};

const senderName = (address) => String(address || "").split("-").pop().replace(/[^A-Za-z]/g, "").slice(0, 12);

// Bank and company senders look like "VM-HDFCBK" or "LICIND". Ordinary phone numbers are personal.
const looksAutomated = (address) => /[A-Za-z]/.test(String(address || ""));

// kind matters for cards: a card *bill* is a category, but an ordinary card purchase is not.
const categorize = (lower, kind) => {
  for (const [pattern, type, label] of CATEGORIES) {
    if (type === "Card" && kind !== "bill") continue;
    if (pattern.test(lower)) return { type, label };
  }
  return null;
};

// The last 4 digits of the account or card a message is about ("A/c XX1234", "Card
// ending 9876", "a/c no. ****1234"), to match it to one of the person's accounts or
// cards. Null when the message doesn't give them.
const ACCOUNT_DIGITS = /\b(a\/c|ac|acct|account|card)(?:\s*(?:no\.?|number|ending(?:\s+(?:with|in))?))?\s*[:-]?\s*(?:[x*]+\s*)?(\d{4})\b/i;
export const accountDigits = (text) => {
  const m = ACCOUNT_DIGITS.exec(String(text || ""));
  return m ? m[2] : null;
};
// Whether those digits are a card's or a bank account's: "card" or "account", or null.
export const digitsVia = (text) => {
  const m = ACCOUNT_DIGITS.exec(String(text || ""));
  if (!m) return null;
  return m[1].toLowerCase() === "card" ? "card" : "account";
};

export const parseBankSms = ({ address, body, date }) => {
  if (!body || !looksAutomated(address)) return null;

  const text = String(body).replace(/\s+/g, " ").trim();
  const lower = text.toLowerCase();
  if (IGNORE.some((re) => re.test(lower))) return null;

  const total = TOTAL_DUE.exec(text);
  const amountMatch = total || AMOUNT.exec(text);
  if (!amountMatch) return null;
  const amount = parseFloat(amountMatch[1].replace(/,/g, ""));
  if (!amount || amount <= 0) return null;

  // "credit card" and "debit card" name a product, not the direction of the money.
  const forDirection = lower.replace(/\b(credit|debit)\s+card\b/g, "card");

  let kind;
  if (PAYMENT_ACK.test(lower)) {
    kind = "expense";
  } else if (UPCOMING.test(forDirection)) {
    kind = "bill";
  } else {
    const debit = DEBIT_WORDS.exec(forDirection);
    const credit = CREDIT_WORDS.exec(forDirection);
    if (!debit && !credit) return null;
    kind = debit && (!credit || debit.index < credit.index) ? "expense" : "income";
  }

  // The sender name helps too: LIC's messages come from "LICIND" without saying LIC.
  const category = categorize(`${lower} ${String(address).toLowerCase()}`, kind);
  const merchant = findMerchant(text, kind === "income" ? "income" : "expense");
  const bank = senderName(address);

  // Bills first (electricity, EMI, ...), then everyday spending by merchant; "Purchase"
  // only when neither says what it was.
  let type;
  if (category) type = category.type;
  else if (kind === "income") type = /\bsalary\b/.test(lower) ? "Salary" : "Payment";
  else if (kind === "bill") type = "Bills";
  else type = spendingType(`${merchant} ${lower}`) || "Purchase";
  if (kind === "income" && category) type = /\bsalary\b/.test(lower) ? "Salary" : "Payment";

  // Name: a known category reads best ("FASTag toll"); otherwise the merchant.
  const label = kind !== "income" && category ? category.label : "";
  const payee = label || merchant || (kind === "income" ? `Credit from ${bank}` : `${kind === "bill" ? "Bill" : "Payment"} via ${bank}`);
  const detail = label && merchant && merchant.toLowerCase() !== label.toLowerCase() ? ` · ${merchant}` : "";

  let when = isoFromMs(date);
  if (kind === "bill") when = dateInText(text, date) || when;

  return {
    kind,
    amount,
    payee,
    type,
    description: `From SMS (${bank})${detail}`,
    date: when,
    autopay: kind === "bill" && /will\s+be\s+(?:auto-?)?(?:debited|deducted|charged)/.test(lower),
    bank,
    digits: accountDigits(text),
    via: digitsVia(text),
  };
};

// One-way fingerprint of a message, used so the same SMS is never imported twice.
export const smsKey = async ({ address, body, date }) => {
  const data = new TextEncoder().encode(`${address}|${date}|${body}`);
  const digest = await crypto.subtle.digest("SHA-256", data);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("")
    .slice(0, 32);
};
